import { useCallback, useEffect, useState } from "react";
import {
  getHeldEvalCameraStream,
  primeEvalCameraPermission,
  queryEvalCameraPermissionState,
} from "../utils/evalCameraPermission";

/**
 * Prompt for eval camera permission at session entry (not during exercise prewarm).
 * Keeps a held MediaStream so iOS does not re-ask when the coach starts the exercise.
 */
export function useEvalCameraPermissionGate({
  enabled = false,
  facingMode = null,
} = {}) {
  const [status, setStatus] = useState(() =>
    enabled && getHeldEvalCameraStream() ? "granted" : "unknown"
  );
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setStatus("unknown");
      return undefined;
    }
    if (getHeldEvalCameraStream()) {
      setStatus("granted");
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const queried = await queryEvalCameraPermissionState();
      if (cancelled) return;
      if (queried === "denied") {
        setStatus("denied");
        return;
      }
      // Even if Permissions API says granted, we need a held stream from a tap
      // so later recording does not re-prompt / fail without a user gesture.
      setStatus("prompt");
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const requestPermission = useCallback(async () => {
    if (!enabled) return { ok: false };
    setRequesting(true);
    try {
      const result = await primeEvalCameraPermission(
        facingMode ? { facingMode } : undefined
      );
      if (result.ok) setStatus("granted");
      else if (result.denied) setStatus("denied");
      else setStatus("prompt");
      return result;
    } finally {
      setRequesting(false);
    }
  }, [enabled, facingMode]);

  return {
    status,
    needsPrompt: enabled && status === "prompt",
    denied: enabled && status === "denied",
    granted: enabled && status === "granted",
    requestPermission,
    requesting,
  };
}

export default useEvalCameraPermissionGate;
